import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";

const LeaderboardTemplateCard = ({ template, boards, t, onEdit }) => {
  const columnsCount =
    (template.columns?.length || 0) +
    (template.bodyMeasurements?.length || 0) +
    (template.repMaxExercises?.length || 0);
  const teamPrograms = template.scope?.teamPrograms || [];

  const handleDelete = () => {
    // Deleting a template also takes down its member boards.
    const liveOn = [
      ...new Set(
        boards
          .filter(
            (b) =>
              String(b.templateId) === String(template._id) &&
              b.state === "public"
          )
          .map((b) => t(`challengeReport.publish.tab.${b.audience}`))
      ),
    ];
    const warning = liveOn.length
      ? `\n\n${t(
          "challengeReport.publish.deleteLiveWarning",
          "This template is also live on: {{audiences}}. Those boards will disappear from the app too.",
          { audiences: liveOn.join(", ") }
        )}`
      : "";
    if (
      confirm(
        t(
          "allTemplates.leaderboard.confirmDelete",
          "Are you sure you want to delete this leaderboard template?"
        ) + warning
      )
    ) {
      submit("/actions/challenge-report-templates/remove", {
        method: "POST",
        body: { id: template._id },
      });
    }
  };

  return (
    <div className="check-in-template-card">
      <div className="check-in-template-card__header">
        <h3 className="check-in-template-card__title">
          {template.name}
          {template.isDefault && (
            <span className="customize-table-modal__chip customize-table-modal__chip--active ml-2 text-xssm! align-middle">
              ({t("allTemplates.leaderboard.defaultBadge", "Default")})
            </span>
          )}
        </h3>
        <span className="check-in-template-card__count">
          {t("allTemplates.leaderboard.columnsCount", "{{count}} columns", {
            count: columnsCount,
          })}
        </span>
      </div>

      {teamPrograms.length > 0 && (
        <div className="check-in-template-card__questions">
          <div className="check-in-template-card__questions-scroll">
            {teamPrograms.map((name) => (
              <div key={name} className="check-in-template-card__question-row">
                <span className="customize-table-modal__chip customize-table-modal__chip--active">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="check-in-template-card__footer mt-auto">
        <button
          type="button"
          className="check-in-template-card__edit-btn"
          onClick={() => onEdit(template)}
        >
          <img
            src="/img/new-icons/edit-pencil.svg"
            alt={t("allTemplates.leaderboard.editAlt", "edit")}
          />
          {t("allTemplates.leaderboard.editTemplate", "Edit template")}
        </button>
        <div className="check-in-template-card__actions">
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={handleDelete}
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              alt={t("allTemplates.leaderboard.deleteAlt", "delete")}
              noFilterOnLight={true}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

LeaderboardTemplateCard.propTypes = {
  template: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string,
    isDefault: PropTypes.bool,
    columns: PropTypes.array,
    bodyMeasurements: PropTypes.array,
    scope: PropTypes.shape({
      teamPrograms: PropTypes.array,
    }),
  }).isRequired,
  boards: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const LeaderboardTemplates = ({
  templates = [],
  boards = [],
  leaderboardEnabled = false,
  onEdit,
}) => {
  const { t } = useTranslation("coaching-portal");

  if (!templates.length) {
    if (!leaderboardEnabled) {
      return (
        <div className="no-programs-section">
          <p>
            {t(
              "allTemplates.leaderboard.notEnabled",
              "The leaderboard feature is not enabled."
            )}
          </p>
          <span>
            {t(
              "allTemplates.leaderboard.notEnabledHint",
              "Enable it in your preferences to access the leaderboard and create templates."
            )}
          </span>
          <a
            role="button"
            data-button-type="primary"
            href="/coaching-portal/settings/preferences"
          >
            {t("allTemplates.leaderboard.goToPreferences", "Go to preferences")}
          </a>
        </div>
      );
    }

    return (
      <div className="no-programs-section">
        <p>
          {t(
            "allTemplates.leaderboard.noTemplates",
            "No leaderboard templates saved yet."
          )}
        </p>
        <span>
          {t(
            "allTemplates.leaderboard.hint",
            'Open the leaderboard page and click "Customize table" to save your first template.'
          )}
        </span>
        <a
          role="button"
          data-button-type="primary"
          href="/coaching-portal/challenge-report"
        >
          {t("allTemplates.leaderboard.goToLeaderboard", "Go to leaderboard")}
        </a>
      </div>
    );
  }

  const sortedTemplates = [...templates].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    })
  );

  return (
    <section className="check-in-templates-section generic-template-section">
      <div className="check-in-templates-grid generic-template-grid">
        {sortedTemplates.map((template) => (
          <LeaderboardTemplateCard
            key={template._id}
            template={template}
            boards={boards}
            t={t}
            onEdit={onEdit}
          />
        ))}
      </div>
    </section>
  );
};

LeaderboardTemplates.propTypes = {
  templates: PropTypes.array,
  boards: PropTypes.array,
  onEdit: PropTypes.func.isRequired,
};

export default LeaderboardTemplates;
